/**
 * DslrControls - Remote camera parameter control panel for DSLR/mirrorless cameras.
 * Renders steppers for ISO, EV, WB, Aperture, Shutter Speed and Image Quality.
 * Reads current + allowed values from propertyCapabilities reported by Android.
 *
 * Optimistic update: when the user presses +/-, the new value is shown immediately.
 * The pending override is held until the server confirms it (server value matches
 * our pending) or a 30s safety timeout expires (camera probably rejected).
 * Intermediate server heartbeats with stale values are ignored.
 * Pending values are shown in blue with a spinning sync icon; on timeout the
 * value flashes red briefly before reverting to the server-reported value.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Modal, Segmented, Tag, Typography, message } from 'antd';
import { DslrPropertyCapabilities, View } from '@/types';
import { DslrPropertyStepper } from '@/components/DslrPropertyStepper';
import { LiveViewFocusOverlay } from '@/components/LiveViewFocusOverlay';
import { MaterialIcon } from '@/components/MaterialIcon';
import { updateDslrDesiredProperty, updateTimerSettings } from '@/services/viewService';
import { useViewEntitlements } from '@/hooks/useViewEntitlements';

/* refresh is owned by CameraTab (visible even before capabilities load) */

const { Text } = Typography;

const DEBOUNCE_MS = 1200;
const PENDING_TIMEOUT_MS = 30_000;
const GUARD_MS = 10_000;

interface DslrControlsProps {
  view: View;
  userId: string;
  projectId: string;
  viewId: string;
  capabilities: DslrPropertyCapabilities | null | undefined;
  connected: boolean;
  shootingMode?: string | null;
  focusMode?: string | null;
  onRefresh?: () => void;
  refreshing?: boolean;
}

// --- Value formatters ---

function formatIso(v: number | string): string {
  return String(v);
}

function formatEv(v: number | string): React.ReactNode {
  const n = Number(v);
  if (n === 0) return '0';
  const sign = n > 0 ? '+' : '-';
  const abs = Math.abs(n);
  const whole = Math.floor(abs);
  const frac = abs - whole;
  let fracParts: [string, string] | null = null;
  if (Math.abs(frac - 1/3) < 0.05) fracParts = ['1', '3'];
  else if (Math.abs(frac - 2/3) < 0.05) fracParts = ['2', '3'];
  if (fracParts) {
    const sup = <sup style={{ fontSize: '0.7em' }}>{fracParts[0]}/{fracParts[1]}</sup>;
    return <>{sign}{whole}<span style={{ marginLeft: 1 }}>{sup}</span></>;
  }
  return n > 0 ? `+${n}` : String(n);
}

function formatWb(v: number | string): string {
  const s = String(v);
  if (s === 'awb') return 'AWB';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function formatAperture(v: number | string): string {
  return `f/${v}`;
}

function formatShutter(v: number | string): string {
  const s = Number(v);
  if (s >= 1) {
    const rounded = Math.round(s * 10) / 10;
    return `${rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1)}"`;
  }
  const denom = Math.round(10 / s) / 10;
  return `1/${denom % 1 === 0 ? denom.toFixed(0) : denom.toFixed(1)}s`;
}

function formatQuality(v: number | string): string {
  return String(v);
}

// Pending optimistic override: value + timestamp for safety timeout.
// confirmedAt: set when server confirms our value — we keep the entry as a
// guard against stale heartbeats for GUARD_MS after confirmation.
interface PendingEntry {
  value: number | string | boolean;
  setAt: number;
  confirmedAt?: number;
}
type PendingMap = Record<string, PendingEntry>;

/** Tolerance-based equality for numeric values (avoids floating point bounce). */
function valuesEqual(
  a: number | string | boolean,
  b: number | string | boolean,
): boolean {
  if (typeof a === 'number' && typeof b === 'number') {
    return Math.abs(a - b) < 0.001;
  }
  return a === b;
}

/** Get the server-reported current value for a property key. */
function getServerCurrent(
  caps: DslrPropertyCapabilities,
  key: string,
): number | string | boolean | undefined {
  switch (key) {
    case 'iso': return caps.iso?.current;
    case 'ev': return caps.ev?.current;
    case 'wb': return caps.wb?.current;
    case 'aperture': return caps.aperture?.current;
    case 'shutterSpeed': return caps.shutterSpeed?.current;
    case 'imageQuality': return caps.imageQuality?.current;
    default: return undefined;
  }
}

export function DslrControls({
  view,
  userId,
  projectId,
  viewId,
  capabilities,
  connected,
  shootingMode,
  focusMode,
  onRefresh,
  refreshing,
}: DslrControlsProps) {
  // RAW formats are gated by the view's tier; the rest of the formats are not.
  const { get: getEntitlements } = useViewEntitlements();
  const rawAllowed = getEntitlements(viewId).raw;
  const debounceTimers = useRef<Record<string, NodeJS.Timeout>>({});
  const [pending, setPending] = useState<PendingMap>({});
  const [reverted, setReverted] = useState<Record<string, number>>({});
  const [lvOverlayOpen, setLvOverlayOpen] = useState(false);

  // Disabled: live view crashes the PTP session when bulk transfer fails
  const hasFocusCtrl = false;


  // When capabilities update from Firestore, reconcile pending entries:
  // - Expired (>30s since user set): drop + flash revert
  // - Server confirmed (value matches): mark confirmedAt, keep as guard
  // - Guard expired (>10s after confirmation): drop silently
  // - Otherwise: keep pending (stale heartbeat, ignore)
  useEffect(() => {
    if (!capabilities) return;
    const expiredKeys: string[] = [];
    setPending((prev) => {
      if (Object.keys(prev).length === 0) return prev;
      const now = Date.now();
      const next: PendingMap = {};
      let changed = false;
      for (const [key, entry] of Object.entries(prev)) {
        const serverVal = getServerCurrent(capabilities, key);
        const confirmed = serverVal !== undefined && valuesEqual(serverVal, entry.value);
        const expired = now - entry.setAt > PENDING_TIMEOUT_MS;

        if (entry.confirmedAt) {
          if (now - entry.confirmedAt > GUARD_MS) {
            // Guard expired — drop silently
            changed = true;
          } else {
            // Guard active — keep, ignore stale heartbeats
            next[key] = entry;
          }
        } else if (expired) {
          expiredKeys.push(key);
          changed = true;
        } else if (confirmed) {
          // First confirmation — keep as guard against stale heartbeats
          next[key] = { ...entry, confirmedAt: now };
          changed = true;
        } else {
          next[key] = entry;
        }
      }
      return changed ? next : prev;
    });
    if (expiredKeys.length > 0) {
      const now = Date.now();
      setReverted((prev) => {
        const next = { ...prev };
        for (const key of expiredKeys) next[key] = now;
        return next;
      });
    }
  }, [capabilities]);

  useEffect(() => {
    const keys = Object.keys(reverted);
    if (keys.length === 0) return;
    const timer = setTimeout(() => setReverted({}), 2000);
    return () => clearTimeout(timer);
  }, [reverted]);

  const writeProperty = useCallback((property: string, value: number | string | boolean | null) => {
    // Optimistic update: show new value immediately
    if (value != null) {
      setPending((prev) => ({
        ...prev,
        [property]: { value, setAt: Date.now() },
      }));
    }

    // Debounce per-property
    if (debounceTimers.current[property]) {
      clearTimeout(debounceTimers.current[property]);
    }
    debounceTimers.current[property] = setTimeout(() => {
      updateDslrDesiredProperty(userId, projectId, viewId, property, value)
        .catch((err) => console.error(`[DslrControls] write FAILED: ${property}`, err));
    }, DEBOUNCE_MS);
  }, [userId, projectId, viewId]);

  if (!capabilities) {
    return (
      <Text type="secondary" style={{ display: 'block', padding: '8px 0' }}>
        Camera parameters not available yet.
      </Text>
    );
  }

  const { iso, ev, wb, aperture, shutterSpeed, imageQuality } = capabilities;

  const qualityCurrent = pending['imageQuality']?.value as string ?? imageQuality?.current;

  const steppers: { key: string; label: string; icon: string; iconSize?: number; iconStyle?: React.CSSProperties; current: number | string; allowed: (number | string)[]; format: (v: number | string) => React.ReactNode; onStep: (v: number | string) => void }[] = [
    { key: 'iso', label: 'ISO', icon: 'camera_roll', current: pending['iso']?.value as number ?? iso?.current ?? 0, allowed: iso?.allowed ?? [], format: formatIso, onStep: (v) => writeProperty('iso', v as number) },
    { key: 'ev', label: 'EV', icon: 'exposure', current: pending['ev']?.value as number ?? ev?.current ?? 0, allowed: ev?.allowed ?? [], format: formatEv, onStep: (v) => writeProperty('ev', v as number) },
    { key: 'wb', label: 'WB', icon: 'wb_incandescent', iconSize: 24, iconStyle: { position: 'relative' as const, top: 3 }, current: pending['wb']?.value as string ?? wb?.current ?? '', allowed: wb?.allowed ?? [], format: formatWb, onStep: (v) => writeProperty('wb', v as string) },
    { key: 'aperture', label: 'Iris', icon: 'camera', current: pending['aperture']?.value as number ?? aperture?.current ?? 0, allowed: aperture?.allowed ?? [], format: formatAperture, onStep: (v) => writeProperty('aperture', v as number) },
    { key: 'shutterSpeed', label: 'Shutter', icon: 'shutter_speed', current: pending['shutterSpeed']?.value as number ?? shutterSpeed?.current ?? 0, allowed: shutterSpeed?.allowed ?? [], format: formatShutter, onStep: (v) => writeProperty('shutterSpeed', v as number) },
  ];

  const boxStyle: React.CSSProperties = {
    border: '1px solid #d9d9d9',
    borderRadius: 8,
    padding: '12px 16px',
  };

  const separatorStyle: React.CSSProperties = {
    borderBottom: '1px solid #f0f0f0',
  };

  return (
    <div style={boxStyle}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', ...separatorStyle, paddingBottom: 10, marginBottom: 2 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {shootingMode && (
            <Tag color="default" variant="outlined">Mode {shootingMode}</Tag>
          )}
          {focusMode && (
            <Tag color="default" variant="outlined">{focusMode}</Tag>
          )}
        </div>
        {onRefresh && (
          <Button
            size="small"
            icon={<span className="material-symbols-rounded" style={{ fontSize: 18, position: 'relative', top: 3 }}>refresh</span>}
            loading={refreshing}
            onClick={onRefresh}
            disabled={!connected}
          />
        )}
      </div>

      {imageQuality && qualityCurrent && (
        <div style={{ ...separatorStyle, padding: '8px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, width: 90, flexShrink: 0 }}>
              <span className="material-symbols-rounded" style={{ fontSize: 20, color: '#8c8c8c' }}>photo_library</span>
              <Text strong style={{ textTransform: 'uppercase', fontSize: 12, whiteSpace: 'nowrap', position: 'relative', top: 2 }}>Format</Text>
            </div>
            <div className={pending['imageQuality'] && !pending['imageQuality']?.confirmedAt ? 'dslr-value-pending' : reverted['imageQuality'] ? 'dslr-value-reverted' : undefined}>
              <Segmented
                className="segmented-emphasis"
                size="small"
                value={qualityCurrent}
                options={imageQuality.allowed.map(v => {
                  // Only the RAW formats are gated: the JPEG ones stay
                  // selectable, so the control is never dead as a whole.
                  const isRawOption = String(v).toLowerCase().includes('raw');
                  const locked = isRawOption && !rawAllowed;
                  return {
                    label: locked ? `${formatQuality(v)} — Pro` : (formatQuality(v) as string),
                    value: v,
                    disabled: locked,
                  };
                })}
                onChange={(v) => {
                  const val = String(v).toLowerCase();
                  const isRaw = val.includes('raw');
                  if (isRaw && !rawAllowed) return;
                  const wasRaw = String(qualityCurrent).toLowerCase().includes('raw');
                  // No configuration means there is no interval to raise; the timer
                  // tab is where an unconfigured view gets one.
                  const currentInterval = view.timerSettings?.interval;
                  if (isRaw && !wasRaw && currentInterval !== undefined && currentInterval < 30) {
                    Modal.confirm({
                      title: 'RAW capture requires a minimum interval of 30 seconds',
                      content: `The current interval (${currentInterval}s) will be automatically raised to 30s.`,
                      okText: 'Enable RAW',
                      onOk: async () => {
                        writeProperty('imageQuality', v as string);
                        const timerSettings = { ...view.timerSettings!, interval: 30 };
                        const capturing = view.capturing ?? false;
                        await updateTimerSettings(userId, projectId, viewId, capturing, timerSettings);
                        message.success('RAW enabled — interval raised to 30s');
                      },
                    });
                    return;
                  }
                  writeProperty('imageQuality', v as string);
                }}
                disabled={!connected}
                style={{ marginLeft: 16 }}
              />
            </div>
          </div>
        </div>
      )}

      {steppers.map((s) => (
        <div key={s.key} style={separatorStyle}>
          <DslrPropertyStepper
            label={s.label}
            icon={s.icon}
            iconSize={s.iconSize}
            iconStyle={s.iconStyle}
            current={s.current}
            allowed={s.allowed}
            formatValue={s.format}
            isPending={!!pending[s.key] && !pending[s.key]?.confirmedAt}
            isReverted={!!reverted[s.key]}
            onStep={s.onStep}
            disabled={!connected}
          />
        </div>
      ))}

      {/* Focus section — only shown for cameras that report FOCUS_CTRL (currently Panasonic only) */}
      {hasFocusCtrl && (
        <div style={{ paddingTop: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', padding: '8px 0 4px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, width: 90, flexShrink: 0 }}>
              <MaterialIcon name="center_focus_strong" size={20} style={{ color: '#8c8c8c' }} />
              <Text strong style={{ textTransform: 'uppercase', fontSize: 12, whiteSpace: 'nowrap', position: 'relative', top: 2 }}>Focus</Text>
            </div>
            <Button
              size="small"
              onClick={() => setLvOverlayOpen(true)}
              disabled={!connected}
              style={{ marginLeft: 16 }}
            >
              Adjust Focus
            </Button>
          </div>
        </div>
      )}

      {lvOverlayOpen && (
        <LiveViewFocusOverlay
          view={view}
          focusMode={focusMode}
          connected={connected}
          onClose={() => setLvOverlayOpen(false)}
        />
      )}
    </div>
  );
}
